import { getImageMeta } from "../../pages/Campaign/Brands/utils.tsx";

export class BrushActions {
	private tempCanvas: HTMLCanvasElement;
	private canvasRef: HTMLCanvasElement | null;
	private canvasOverlayRef: HTMLCanvasElement | null;
	private dataUrl: string;

	constructor(canvasRef: HTMLCanvasElement | null, canvasOverlayRef: HTMLCanvasElement | null) {
		this.tempCanvas = document.createElement("canvas");
		this.canvasRef = canvasRef;
		this.canvasOverlayRef = canvasOverlayRef;
		this.dataUrl = "#";
	}

	public handleCanvasClear(): void {
		if (!this.canvasOverlayRef) return;

		const ctx = this.canvasOverlayRef.getContext("2d");
		if (ctx) {
			ctx.clearRect(0, 0, this.canvasOverlayRef.width, this.canvasOverlayRef.height);

			// Optional: Reinitialize canvas state if needed
			ctx.globalCompositeOperation = "source-over"; // Reset blending mode
		}
	}

	public createBase64ImageMask(base64Image: string): Promise<string> {
		return new Promise<string>((resolve) => {
			const tempCanvas = this.tempCanvas;
			const ctx = tempCanvas.getContext("2d");

			getImageMeta(base64Image).then((img: any) => {
				tempCanvas.width = img.width;
				tempCanvas.height = img.height;
				ctx?.drawImage(img, 0, 0);

				const imageData = ctx?.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
				const pixels = imageData?.data || [];
				const originalImgPixels = this.canvasOverlayRef
					?.getContext("2d")
					?.getImageData(0, 0, tempCanvas.width, tempCanvas.height)?.data;

				let whitePixelsCounter = 0;
				if (originalImgPixels) {
					for (let i = 0; i < originalImgPixels.length; i += 4) {
						const r = originalImgPixels[i];
						const g = originalImgPixels[i + 1];
						const b = originalImgPixels[i + 2];

						if (r > 0 || g > 0 || b > 0) {
							// Set pixel to white
							pixels[i] = 255;
							pixels[i + 1] = 255;
							pixels[i + 2] = 255;
							whitePixelsCounter++;
						} else {
							// Set pixel to black
							pixels[i] = 0;
							pixels[i + 1] = 0;
							pixels[i + 2] = 0;
						}
					}

					ctx?.putImageData(imageData!, 0, 0);
				}

				if (!whitePixelsCounter) {
					resolve("");
				} else {
					const processedBase64 = tempCanvas.toDataURL();
					resolve(processedBase64);
				}
			});
		});
	}

	public async handleCanvasDownload(): Promise<string | undefined> {
		if (!this.canvasRef) return;

		let base64Image = this.canvasRef.toDataURL("image/png");
		base64Image = await this.createBase64ImageMask(base64Image);

		return base64Image;
	}

	public async downloadCanvasImage() {
		const base64Image = await this.handleCanvasDownload();

		if (base64Image) {
			const link = document.createElement("a");

			link.href = base64Image;
			link.download = "canvas_image.png";

			document.body.appendChild(link);
			link.click();

			document.body.removeChild(link);
		} else {
			console.error("Failed to download image: No valid base64 data returned");
		}
	}

	public getDataUrl() {
		return this.dataUrl;
	}

	public validateMaskAspectRatio(
		maskBase64: string,
		originalDimensions: { width: number; height: number },
	): Promise<boolean> {
		return new Promise((resolve) => {
			const img = new Image();
			img.src = maskBase64;
			img.onload = () => {
				const originalAspectRatio = originalDimensions.width / originalDimensions.height;
				const maskAspectRatio = img.width / img.height;
				// Allow a small tolerance for floating point comparison
				const tolerance = 0.01;
				const isValid = Math.abs(originalAspectRatio - maskAspectRatio) < tolerance;
				resolve(isValid);
			};
			img.onerror = () => resolve(false);
		});
	}

	private hexToRgb(hex: string): { r: number; g: number; b: number } {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result
			? {
					r: parseInt(result[1], 16),
					g: parseInt(result[2], 16),
					b: parseInt(result[3], 16),
				}
			: { r: 136, g: 0, b: 255 };
	}

	private createTempCanvasWithImage(
		img: HTMLImageElement,
		width: number,
		height: number,
	): { tempCanvas: HTMLCanvasElement; tempCtx: CanvasRenderingContext2D } | null {
		const tempCanvas = document.createElement("canvas");
		tempCanvas.width = width;
		tempCanvas.height = height;
		const tempCtx = tempCanvas.getContext("2d");
		if (!tempCtx) {
			return null;
		}
		tempCtx.drawImage(img, 0, 0, width, height);
		return { tempCanvas, tempCtx };
	}

	private convertMaskPixelsToBrushColor(imageData: ImageData, brushColor: string): void {
		const pixels = imageData.data;
		const rgbColor = this.hexToRgb(brushColor);
		const WHITE_THRESHOLD = 1;

		for (let i = 0; i < pixels.length; i += 4) {
			const r = pixels[i];
			const g = pixels[i + 1];
			const b = pixels[i + 2];

			if (r >= WHITE_THRESHOLD && g >= WHITE_THRESHOLD && b >= WHITE_THRESHOLD) {
				pixels[i] = rgbColor.r;
				pixels[i + 1] = rgbColor.g;
				pixels[i + 2] = rgbColor.b;
				pixels[i + 3] = 255;
			} else {
				pixels[i + 3] = 0;
			}
		}
	}

	private drawProcessedMaskToOverlay(
		ctx: CanvasRenderingContext2D,
		tempCtx: CanvasRenderingContext2D,
		tempCanvas: HTMLCanvasElement,
		imageData: ImageData,
		canvasOverlay: HTMLCanvasElement,
	): void {
		ctx.clearRect(0, 0, canvasOverlay.width, canvasOverlay.height);
		tempCtx.putImageData(imageData, 0, 0);
		ctx.drawImage(tempCanvas, 0, 0);
	}

	public loadMaskImage(maskBase64: string, brushColor: string): Promise<void> {
		return new Promise((resolve, reject) => {
			if (!this.canvasOverlayRef) {
				reject(new Error("Canvas overlay ref not found"));
				return;
			}

			const ctx = this.canvasOverlayRef.getContext("2d");
			if (!ctx) {
				reject(new Error("Failed to get canvas context"));
				return;
			}

			const img = new Image();
			img.src = maskBase64;
			img.onload = () => {
				const canvasOverlay = this.canvasOverlayRef!;

				const tempResult = this.createTempCanvasWithImage(img, canvasOverlay.width, canvasOverlay.height);
				if (!tempResult) {
					reject(new Error("Failed to get temp canvas context"));
					return;
				}
				const { tempCanvas, tempCtx } = tempResult;

				const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
				this.convertMaskPixelsToBrushColor(imageData, brushColor);
				this.drawProcessedMaskToOverlay(ctx, tempCtx, tempCanvas, imageData, canvasOverlay);

				resolve();
			};
			img.onerror = (err) => reject(new Error(`Failed to load mask image: ${err}`));
		});
	}
}
